"use client";

import type { AgentThreadShareVo } from "@/api";
import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import { useToast } from "@/component/Toast/useToast";
import { useBreakpoints } from "@/hooks/useBreakpoints";
import { type UrlAttachment } from "@/types/attachment";
import { type ChatDraft } from "@/types/draft-strorage";
import { convertFilePathToUrl } from "@/util/convertFilePathToUrl";
import { attachmentsToPersisted } from "@/util/draftConverter";
import { getDraftKey, setDraft } from "@/util/draftStorage";
import "@ant-design/v5-patch-for-react-19";
import { useRouter } from "@bprogress/next/app";
import { ConfigProvider } from "antd";
import { useCallback, useEffect, useState } from "react";
import { ThreadMedia } from "./components/ThreadMedia";
import { ThreadShareHeader } from "./components/ThreadShareHeader";
import { ThreadSidebar } from "./components/ThreadSidebar";
import styles from "./index.module.css";

interface ThreadShareProps {
  thread: AgentThreadShareVo | null;
}

const HOME_ROUTE = "/";

export function ThreadShare(props: ThreadShareProps) {
  const { thread } = props;
  const router = useRouter();
  const { error: showErrorToast } = useToast();
  const { isLg, isXl, is2xl } = useBreakpoints();
  const isLargeScreen = isLg || isXl || is2xl;
  const [videoPlayerHeight, setVideoPlayerHeight] = useState<number>();

  const handleDialogClose = () => {
    router.push(HOME_ROUTE);
  };

  useEffect(() => {
    if (!thread) {
      console.error("ThreadShare: thread data is missing");
      handleDialogClose();
    }
  }, [thread]);

  // 处理 VideoPlayer 高度变化的回调
  const handleVideoPlayerHeightChange = useCallback((height: number) => {
    setVideoPlayerHeight(height);
  }, []);

  // 断点变化时重置高度
  useEffect(() => {
    if (!isLargeScreen) {
      setVideoPlayerHeight(undefined);
    }
  }, [isLargeScreen]);

  if (!thread) {
    return null;
  }

  const handleRemix = async () => {
    try {
      const attachments: UrlAttachment[] = (thread.files || [])
        .filter((file) => file.filePath)
        .map((file, index) => {
          const url = convertFilePathToUrl(file.filePath);
          return {
            type: "url" as const,
            id: `remix-${index}-${Date.now()}`,
            url,
            preview: url,
          };
        })
        .filter((attachment) => attachment.url);

      const persistedAttachments = await attachmentsToPersisted(attachments);

      const draft: ChatDraft = {
        text: thread.prompt || "",
        isAuto: true,
        attachments: persistedAttachments,
        updatedAt: Date.now(),
      };

      await setDraft(getDraftKey(), draft);

      router.push(HOME_ROUTE);

      logger.event(VidMuseEvent.ThreadSharePageRemix, {
        thread_id: thread.id,
      });
    } catch (error) {
      console.error("Failed to remix thread:", error);
      showErrorToast("Failed to remix project. Please try again.");
    }
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            controlHeightLG: 40,
            defaultBorderColor: "transparent",
          },
        },
      }}
    >
      <div className={styles.page}>
        <ThreadShareHeader onClose={handleDialogClose} />
        <div className="w-full lg:h-[calc(100vh-60px)]">
          <div className="w-full h-full mx-auto flex flex-col gap-5 p-5 lg:pb-20 lg:max-w-[1600px] lg:flex-row">
            <ThreadMedia
              src={thread.resultPath}
              onVideoPlayerHeightChange={
                isLargeScreen ? handleVideoPlayerHeightChange : undefined
              }
            />
            <ThreadSidebar
              thread={thread}
              onRemix={handleRemix}
              mediaHeight={videoPlayerHeight}
            />
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
}
